/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { isValidElement, memo, useCallback, useState } from "react";
import type { FunctionComponent, JSX, ReactNode } from "react";

import { ListItem } from "./ListItem";
import { ListSkeleton } from "./ListSkeleton";
import type { ListProps } from "./ListProps";

import "./List.scss";

/** Sentinel meaning "no row is focused". */
const NO_ROW_FOCUSED: number = -1;

/** Placeholder rows drawn while the first page is in flight. */
const DEFAULT_SKELETON_ROWS: number = 3;

/** Placeholder rows appended below the list while a further page is in flight. */
const DEFAULT_LOADING_MORE_ROWS: number = 2;

/**
 * React key for the wrapper around one item.
 *
 * The item's own key when it brought one, so a row removed from the middle of
 * the list moves the wrappers that follow it instead of renumbering them. Keyed
 * by position, React would instead hand wrapper `n` the row that used to be at
 * `n + 1`: the child's key changes, so every row below the removed one unmounts
 * and remounts — the thumbnails reload and any in-progress transition restarts,
 * for a change that only shortened the list.
 *
 * Falls back to the index for a bare string or number row, which carries no key
 * to borrow.
 */
const resolveItemKey = (item: ReactNode, index: number): string => {
  if (isValidElement(item) && item.key !== null) {
    return item.key;
  }
  return `list-item-${index}`;
};

/**
 * List — a vertical stack of rows with a single focused row.
 *
 * Focus can be owned either way. Left alone, the list tracks the focused row in
 * its own state from pointer events. Pass {@link ListProps.focusedIndex} and it
 * becomes fully controlled, which is what screens whose D-pad focus lives in a
 * Redux slice need — the list then only *reports* focus moves through
 * `onItemFocus` and renders whatever index it is handed back.
 *
 * Loading is owned here too, in three distinct states, because they are three
 * different questions the user is asking:
 *
 * - **First load** (`isLoading`, nothing to show yet) — placeholder rows stand
 *   in for the list. They occupy the space the rows will occupy, so the page
 *   does not jolt when the data lands.
 * - **Refresh** (`isLoading` over rows that already arrived) — the rows stay
 *   put and the stack merely dims. Replacing content the user is already
 *   reading with placeholders would be a regression, not a loading state.
 * - **Paging** (`isLoadingMore`) — placeholders are appended *below* the real
 *   rows, so every existing index keeps its meaning and focus does not move.
 *
 * Layout and spacing belong to the parent screen's scoped class; this component
 * only owns the title typography and the row focus ring.
 */
const ListComponent: FunctionComponent<ListProps> = (
  props: ListProps
): JSX.Element => {
  const {
    title,
    items,
    isActive = false,
    focusedIndex,
    onItemFocus,
    onItemSelect,
    areItemsSelfInteractive = false,
    isLoading = false,
    isLoadingMore = false,
    skeletonCount = DEFAULT_SKELETON_ROWS,
    loadingMoreCount = DEFAULT_LOADING_MORE_ROWS,
    renderSkeletonItem,
    className
  } = props;

  const [internalFocusedIndex, setInternalFocusedIndex] =
    useState<number>(NO_ROW_FOCUSED);

  // A supplied `focusedIndex` takes over completely — mixing the two would let
  // a hover silently disagree with the caller's state.
  const isControlled: boolean = focusedIndex !== undefined;

  // Rows arrive, page in and are deleted under the cursor, and an index is only
  // meaningful against the list it was taken from. Left alone, a list that
  // shrinks past the tracked row keeps a focus index no row answers to — so
  // nothing is drawn focused, and if a later fetch grows the list again the ring
  // reappears on whichever row has since taken that position. Dropping focus
  // when it falls out of range is the honest outcome: the row it pointed at is
  // gone.
  //
  // Adjusted during render rather than in an effect so no frame is ever painted
  // with the out-of-range index. Only the uncontrolled path needs it: a caller
  // that owns `focusedIndex` owns its clamping too, and this component must
  // render exactly the index it is handed.
  const [trackedItemCount, setTrackedItemCount] = useState<number>(
    items.length
  );
  if (!isControlled && trackedItemCount !== items.length) {
    setTrackedItemCount(items.length);
    if (internalFocusedIndex >= items.length) {
      setInternalFocusedIndex(NO_ROW_FOCUSED);
    }
  }

  const resolvedFocusedIndex: number = isControlled
    ? (focusedIndex as number)
    : internalFocusedIndex;

  const hasItems: boolean = items.length > 0;

  // The three states are mutually exclusive by construction: whether rows exist
  // decides between standing in for the list and merely dimming it, and only a
  // list that exists can be appended to.
  const isFirstLoad: boolean = isLoading && !hasItems;
  const isRefreshing: boolean = isLoading && hasItems;
  const isAppending: boolean = isLoadingMore && !isLoading && hasItems;

  // Memoized, because these are every row's `onFocus` / `onClick`: a fresh pair
  // each render changes every `ListItem`'s props, and `ListItem`'s own `memo`
  // then re-renders the whole list on every focus move. On a long list walked at
  // D-pad repeat speed that is the difference between re-rendering two rows per
  // press and re-rendering all of them.
  const handleItemFocus = useCallback(
    (index: number): void => {
      if (!isControlled) {
        setInternalFocusedIndex(index);
      }
      if (onItemFocus !== undefined) {
        onItemFocus(index);
      }
    },
    [isControlled, onItemFocus]
  );

  const handleItemClick = useCallback(
    (index: number): void => {
      if (!isControlled) {
        setInternalFocusedIndex(index);
      }
      if (onItemSelect !== undefined) {
        onItemSelect(index);
      }
    },
    [isControlled, onItemSelect]
  );

  const rootClassName: string = [
    "list",
    isActive ? "list--active" : "",
    isFirstLoad ? "list--loading" : "",
    isRefreshing ? "list--refreshing" : "",
    className
  ]
    .filter(
      (token?: string): token is string => token !== undefined && token !== ""
    )
    .join(" ");

  return (
    <div className={rootClassName} aria-busy={isLoading || isLoadingMore}>
      {title !== "" && title !== undefined && title !== null && (
        <div className="list__title">{title}</div>
      )}
      <div className="list__container">
        {isFirstLoad ? (
          <ListSkeleton count={skeletonCount} renderItem={renderSkeletonItem} />
        ) : (
          items.map(
            (item: ReactNode, index: number): JSX.Element => (
              <ListItem
                key={resolveItemKey(item, index)}
                index={index}
                isFocused={resolvedFocusedIndex === index}
                onFocus={handleItemFocus}
                onClick={handleItemClick}
                isPresentational={areItemsSelfInteractive}
              >
                {item}
              </ListItem>
            )
          )
        )}

        {/*
          Appended after the real rows on purpose: prepending or interleaving
          would shift every index the caller's focus state is expressed in.
        */}
        {isAppending && (
          <ListSkeleton
            count={loadingMoreCount}
            startIndex={items.length}
            renderItem={renderSkeletonItem}
          />
        )}
      </div>
    </div>
  );
};

export const List = memo(ListComponent);
